/**
 * @fileoverview Canonical per-field validation predicates + issue aggregator.
 * @module core-domain/inspection
 *
 * Responsibilities:
 * - Single source of truth for "is this field answered?" and "what issues does
 *   this field have?". Consumed by the renderer (FieldWrapper red-ring,
 *   SectionRenderer header badge, tab badges, ValidationSummary) AND by the
 *   canonical completion validator + its server mirror.
 * - Owns the failing-question -> occurrence(+location/photo/notes/corrective)
 *   rule so the in-form badges and the Complete gate never disagree.
 *
 * Dependencies:
 * - `./types` (InspectionFormField, FieldResponse, ResolvedFieldStateV2).
 *
 * Architecture:
 * - Pure functions. No React, no DOM, no IO. Lives in core-domain (below the
 *   renderer) so the server-side completion mirror can share it.
 *
 * Notes:
 * - The hidden/disabled/N/A skip semantics are the authoritative completion
 *   exceptions: a widget on a hidden form/section, a hidden widget, or an N/A
 *   widget produces NO issues.
 */
import type {
  InspectionFormField,
  FieldResponse,
  ResolvedFieldStateV2,
} from './types';

/**
 * @contract FieldIssueKind
 * Purpose: enumerates every kind of open validation issue a field can carry.
 * Rules: occurrence* kinds only apply to a failing pass/fail widget.
 */
export type FieldIssueKind =
  | 'requiredMissing'
  | 'requireNotesUnmet'
  | 'requirePhotosUnmet'
  | 'ackRequired'
  | 'patternMismatch'
  | 'maxLengthExceeded'
  | 'minLengthUnmet'
  | 'rangeExceeded'
  | 'minSelectedUnmet'
  | 'minInstancesUnmet'
  | 'violationOccurrenceRequired'
  | 'occurrenceLocationRequired'
  | 'occurrencePhotosRequired'
  | 'occurrenceNotesRequired'
  | 'occurrenceCorrectiveRequired';

/**
 * @contract FieldIssue
 * Purpose: a single open validation issue against a field.
 * Fields: fieldId, optional formId, kind, short user-facing label.
 * Rules: presence of ANY FieldIssue blocks inspection completion.
 */
export interface FieldIssue {
  fieldId: string;
  formId?: string;
  kind: FieldIssueKind;
  /** Short user-facing label used by the summary panel. */
  label: string;
}

/**
 * @contract FieldOccurrenceInput
 * Purpose: host-agnostic view of one logged violation occurrence, used to
 * evaluate the per-occurrence required sub-fields.
 * Fields: location/notes/correctiveActions (free text), attachmentCount.
 * Rules: hosts map their LoggedViolationOccurrence / ViolationOccurrence here.
 */
export interface FieldOccurrenceInput {
  location?: string;
  notes?: string;
  correctiveActions?: string;
  attachmentCount: number;
}

/**
 * @contract FieldViolationContext
 * Purpose: the violation slice needed to evaluate the failing-question ->
 * occurrence rule for one field.
 * Fields: failing (>=1 mapping fired or pass/fail verdict is fail), the union
 * of required occurrence sub-fields across failing mappings, the logged
 * occurrences.
 * Rules: when omitted, getFieldIssues emits NO occurrence issues (back-compat).
 */
export interface FieldViolationContext {
  failing: boolean;
  requireLocation?: boolean;
  requirePhotos?: boolean;
  requireNotes?: boolean;
  requireCorrectiveAction?: boolean;
  occurrences: FieldOccurrenceInput[];
}

/**
 * @process isFieldAnswered
 * Steps:
 * 1. Treat N/A as answered.
 * 2. Treat null / undefined / "" / [] as unanswered.
 * 3. Otherwise answered (boolean false / 0 are valid answers).
 * Inputs: field (for type-specific rules), the persisted value, isNA flag.
 * Outputs: boolean.
 * Idempotency: REQUIRED.
 */
export function isFieldAnswered(
  field: InspectionFormField,
  value: unknown,
  isNA?: boolean,
): boolean {
  if (isNA) return true;
  if (value == null) return false;
  if (typeof value === 'string') return value.length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    if (field.type === 'inspectors') {
      const primary = (value as { primaryInspectorId?: unknown }).primaryInspectorId;
      return typeof primary === 'string' && primary.trim().length > 0;
    }
    return Object.keys(value as object).length > 0;
  }
  return true;
}

/**
 * @process isRequiredEffective
 * Steps:
 * 1. If `field.required === true` (static), return true.
 * 2. Otherwise consult the engine-v2 `resolved.required` flag.
 * Inputs: field + resolved state.
 * Outputs: boolean.
 * Idempotency: REQUIRED.
 */
export function isRequiredEffective(
  field: InspectionFormField,
  resolved?: ResolvedFieldStateV2,
): boolean {
  return !!(field.required || resolved?.required);
}

/**
 * @process getFieldIssues
 * Steps:
 * 1. Skip hidden + disabled fields, and N/A fields (cannot be issues — these
 *    are the authoritative completion exceptions).
 * 2. Emit `requiredMissing` when a required field is unanswered.
 * 3. Emit per-widget active validation issues (format/length/range/...).
 * 4. Emit engine-v2 require-notes / require-photos issues.
 * 5. When a violation context is supplied and the field is failing, emit the
 *    failing-question -> occurrence(+location/photo/notes/corrective) issues.
 * Inputs: field, response `{ value, isNA, attachments? }`, resolved state,
 *   formId (tag), optional violation context.
 * Outputs: FieldIssue[] — empty when the field is fine.
 * Idempotency: REQUIRED.
 */
export function getFieldIssues(
  field: InspectionFormField,
  response: FieldResponse | undefined,
  resolved?: ResolvedFieldStateV2,
  formId?: string,
  violation?: FieldViolationContext,
): FieldIssue[] {
  if (resolved?.hidden) return [];
  if (resolved?.disabled) return [];
  if (response?.isNA) return [];

  const out: FieldIssue[] = [];
  const value = response?.value;
  const required = isRequiredEffective(field, resolved);
  const answered = isFieldAnswered(field, value, response?.isNA);
  const extras = (field.extras ?? {}) as Record<string, unknown>;
  const label = field.label || field.id;

  if (required && !answered) {
    out.push({ fieldId: field.id, formId, kind: 'requiredMissing', label });
  }

  // Per-widget active validation — mirrors what the widgets compute inline.
  switch (field.type) {
    case 'text': {
      if (typeof value === 'string') {
        const max = typeof extras.maxLength === 'number' ? (extras.maxLength as number) : undefined;
        if (max != null && value.length > max) {
          out.push({ fieldId: field.id, formId, kind: 'maxLengthExceeded', label });
        }
        const pattern = typeof extras.pattern === 'string' ? (extras.pattern as string) : undefined;
        if (pattern && value.length > 0) {
          try {
            if (!new RegExp(pattern).test(value)) {
              out.push({ fieldId: field.id, formId, kind: 'patternMismatch', label });
            }
          } catch { /* invalid pattern — ignore */ }
        }
      }
      break;
    }
    case 'textarea': {
      if (typeof value === 'string') {
        const min = typeof extras.minLength === 'number' ? (extras.minLength as number) : undefined;
        const max = typeof extras.maxLength === 'number' ? (extras.maxLength as number) : undefined;
        if (min != null && value.length > 0 && value.length < min) {
          out.push({ fieldId: field.id, formId, kind: 'minLengthUnmet', label });
        }
        if (max != null && value.length > max) {
          out.push({ fieldId: field.id, formId, kind: 'maxLengthExceeded', label });
        }
      }
      break;
    }
    case 'number':
    case 'measurement': {
      if (typeof value === 'number' && Number.isFinite(value)) {
        const min = typeof extras.min === 'number' ? (extras.min as number) : undefined;
        const max = typeof extras.max === 'number' ? (extras.max as number) : undefined;
        if ((min != null && value < min) || (max != null && value > max)) {
          out.push({ fieldId: field.id, formId, kind: 'rangeExceeded', label });
        }
      }
      break;
    }
    case 'checkboxGroup': {
      if (Array.isArray(value)) {
        const minSelected = typeof extras.minSelected === 'number' ? (extras.minSelected as number) : undefined;
        if (minSelected != null && value.length > 0 && value.length < minSelected) {
          out.push({ fieldId: field.id, formId, kind: 'minSelectedUnmet', label });
        }
      }
      break;
    }
    case 'repeatableList': {
      if (Array.isArray(value)) {
        const minInstances = typeof extras.minInstances === 'number' ? (extras.minInstances as number) : undefined;
        if (minInstances != null && value.length < minInstances) {
          out.push({ fieldId: field.id, formId, kind: 'minInstancesUnmet', label });
        }
      }
      break;
    }
    case 'instructions': {
      if (extras.requireAck === true && value !== true) {
        out.push({ fieldId: field.id, formId, kind: 'ackRequired', label });
      }
      break;
    }
    default:
      break;
  }

  // Engine-v2 require-notes / require-photos (driven by template rules).
  if (resolved?.requireNotes) {
    const notesOk = typeof value === 'string' && value.trim().length > 0;
    if (!notesOk) out.push({ fieldId: field.id, formId, kind: 'requireNotesUnmet', label });
  }
  if (resolved?.requirePhotos) {
    const attachments = (response as (FieldResponse & { attachments?: unknown[] }) | undefined)?.attachments;
    const photoCount = Array.isArray(attachments) ? attachments.length : 0;
    if (photoCount === 0) out.push({ fieldId: field.id, formId, kind: 'requirePhotosUnmet', label });
  }

  // Failing-question -> occurrence rule (the completion gate's headline rule).
  if (violation?.failing) {
    out.push(...getFailingFieldViolationIssues(field, violation, formId));
  }

  return out;
}

/**
 * @process getFailingFieldViolationIssues
 * Steps:
 * 1. When the field is failing and has ZERO occurrences -> emit
 *    `violationOccurrenceRequired`.
 * 2. Otherwise, for every required occurrence sub-field (location/photos/notes/
 *    corrective) that ANY logged occurrence leaves unmet -> emit the matching
 *    `occurrence*Required` issue (once per kind).
 * Inputs: field, violation context (failing + required sub-fields + occurrences),
 *   formId (tag).
 * Outputs: FieldIssue[] — empty when the failing field is fully documented.
 * Idempotency: REQUIRED.
 */
export function getFailingFieldViolationIssues(
  field: InspectionFormField,
  violation: FieldViolationContext,
  formId?: string,
): FieldIssue[] {
  if (!violation.failing) return [];
  const label = field.label || field.id;
  const out: FieldIssue[] = [];
  const occurrences = violation.occurrences ?? [];

  if (occurrences.length === 0) {
    out.push({ fieldId: field.id, formId, kind: 'violationOccurrenceRequired', label });
    return out;
  }

  const missesLocation = violation.requireLocation
    && occurrences.some((o) => !o.location || o.location.trim().length === 0);
  const missesPhotos = violation.requirePhotos
    && occurrences.some((o) => (o.attachmentCount ?? 0) === 0);
  const missesNotes = violation.requireNotes
    && occurrences.some((o) => !o.notes || o.notes.trim().length === 0);
  const missesCorrective = violation.requireCorrectiveAction
    && occurrences.some((o) => !o.correctiveActions || o.correctiveActions.trim().length === 0);

  if (missesLocation) out.push({ fieldId: field.id, formId, kind: 'occurrenceLocationRequired', label });
  if (missesPhotos) out.push({ fieldId: field.id, formId, kind: 'occurrencePhotosRequired', label });
  if (missesNotes) out.push({ fieldId: field.id, formId, kind: 'occurrenceNotesRequired', label });
  if (missesCorrective) out.push({ fieldId: field.id, formId, kind: 'occurrenceCorrectiveRequired', label });

  return out;
}

/**
 * @process describeIssueKind
 * Steps: Map a FieldIssueKind to its short user-facing message. The summary
 *   panel and per-widget helper text both pull from this so they stay consistent.
 * Inputs: kind. Outputs: string. Idempotency: REQUIRED.
 */
export function describeIssueKind(kind: FieldIssueKind): string {
  switch (kind) {
    case 'requiredMissing':              return 'Required';
    case 'requireNotesUnmet':            return 'Notes required';
    case 'requirePhotosUnmet':           return 'Photo required';
    case 'ackRequired':                  return 'Acknowledgement required';
    case 'patternMismatch':              return 'Format does not match pattern';
    case 'maxLengthExceeded':            return 'Too long';
    case 'minLengthUnmet':               return 'Too short';
    case 'rangeExceeded':                return 'Value out of range';
    case 'minSelectedUnmet':             return 'Select more options';
    case 'minInstancesUnmet':            return 'Add more entries';
    case 'violationOccurrenceRequired':  return 'Add a violation occurrence';
    case 'occurrenceLocationRequired':   return 'Occurrence location required';
    case 'occurrencePhotosRequired':     return 'Occurrence photo required';
    case 'occurrenceNotesRequired':      return 'Occurrence notes required';
    case 'occurrenceCorrectiveRequired': return 'Corrective action required';
    default:                             return 'Issue';
  }
}
